import React, { useState, useEffect } from "react";
import { useHeaderContext } from "../../../context/headerContext";
import { useCartContext } from "../../../context/cartContext";
import { useDrawerContext } from "../../../context/drawerContext";
import { useNavigate } from "react-router";
import { useUserContext } from "../../../context/userContext";
import Button from "../../../components/Button";

import CartProductCard from "../../../components/Shop/CartProductCard";
import EditIcon from "/public/images/icons/editIcon.svg?react";
import TrashIcon from "/public/images/icons/trashRed.svg?react";
import CloseIcon from "/public/images/icons/close.svg?react";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";

import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function Cart() {
  const [activeIndex, setActiveIndex] = useState(null);

  const { setTitle, setBackButton, setCartButton, setCartButtonFunc } = useHeaderContext();
  const { cart , products, addCart } = useCartContext();
  const { user, logout, updateTokens } = useUserContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  // const [refreshCart, setRefreshCart] = useState(false);
  // const [initialCartFetch, setInitialCartFetch] = useState(true);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    setTitle("CART");
    setCartButton(true);
    setBackButton(true);

    const handleBack = () => {
      navigate('/cart');
    };

    setCartButtonFunc(handleBack);
  }, []);

  const handleSetActive = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

    useEffect(() => {
      if (loading) {
        fetchCart();
      } 
    }, [loading]);

    const fetchCart = async () => {
      try {
        await Sentry.startSpan({ name: "Fetch Cart", op: "http.request" }, async (span) => {
          const url = `${import.meta.env.VITE_API_URL}Cart/Cart?userAccountId=${user.id}`;
          span.setAttribute("http.url", url);
          span.setAttribute("userId", user.id);
    
          const response = await fetchWithRefresh(
            url,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => {
              toast.info(message);
            }
          );
    
          if (response.ok) {
            const text = await response.text();
            try {
              const data = JSON.parse(text);
              addCart(data);
            } catch (e) {
              if (text === "Cart is empty") {
                addCart([]);
              } else {
                throw new Error("Failed to fetch cart");
              }
            }
          } else {
            throw new Error("Failed to fetch cart");
          }
        });
      } catch (error) {
        Sentry.captureException(error, { extra: { userId: user.id } });
        LogErrors(user, logout, updateTokens, "Error fetching cart: " + error);
      } finally {
        setLoading(false);
      }
    };
    
    const removeCartItem = async (product) => {
      try {
        await Sentry.startSpan({ name: "Remove Cart Item", op: "http.request" }, async (span) => {
          const url = `${import.meta.env.VITE_API_URL}Cart/CartParticipant?cartLineItemId=${product.CartLineItemId}`;
          span.setAttribute("http.url", url);
          span.setAttribute("cartLineItemId", product.CartLineItemId);
    
          const response = await fetchWithRefresh(
            url,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => {
              toast.info(message);
            }
          );
    
          if (response.ok) {
            setActiveIndex(null);
            setLoading(true);
          } else {
            throw new Error("Failed to remove cart item");
          }
        });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { cartLineItemId: product.CartLineItemId },
        });
        LogErrors(user, logout, updateTokens, "Error removing cart item: " + error);
      }
    };
    

  const clearBasketConfirmationDrawer = (product) => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            REMOVE BAND
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div>
          <Button 
            variant="primary" 
            className="w-full mt-4"
            onClick={() => {removeCartItem(product), setOpen(false)}}
          >
            Confirm & Continue
          </Button>
          <Button
            variant="quaternary"
            className="w-full mt-4"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </div>
    )
  }
  const editBasketInfo = (product) => {
    navigate(
      `/shop/product-summary?productId=${product.CartLineItemId}&participantId=${product.ParticipantId}`, 
      {state: {edit: true}}
    )
  }

  if (loading && cart?.length > 0) {
  return (
    <div className="flex justify-center items-center h-full">
      <div className="loading loading-spinner loading-lg">Loading...</div>
    </div>
  )
}


  return (
    <div>
      {cart?.length === 0 ? (
        <div className="flex flex-col mt-[25%] justify-center items-center text-center">
          <div className="mb-[40px] flex flex-col gap-[4px]">
            <p className="tracking-widest text-[12px] text-sportsBlack">CART</p>
            <p className="text-[32px] font-semibold leading-[42px]">Cart empty</p>
            <p className="text-[#909090]">There are no items in your cart, go back to add items to continue</p>
          </div>
          <Button variant="primary" className="w-full" href="/shop/my-participants">Go Back</Button>
        </div>
      ) : (
        <div>
        <div className="flex flex-row gap-4 justify-between items-center mb-[31px]">
          <div>
            <h1>Your Cart</h1>
          </div>
          <div>
            <div className="flex flex-col justify-end items-end">
              <p>Total</p>
              <p className="font-semibold leading-[26px]">£{cart.CartTotalFormatted}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-[10px]">
          {cart?.cartItems?.map((product, index) => (
            <CartProductCard 
              key={product.CartLineItemId}
              product={product} 
              active={activeIndex === index} 
              setActive={() => handleSetActive(index)} 
              index={index}
            >
              <div className="flex flex-row gap-4 justify-between items-center">
                <button 
                  className="justify-start text-base leading-6 flex w-full flex-row gap-[10px] items-center bg-sportsLightGrey rounded-1 p-0 bg-transparent text-sportsBlack underline rounded-none hover:shadow-none active:shadow-none" 
                  onClick={() => editBasketInfo(product)}
                >
                  <EditIcon /> Edit Info
                </button>

                <Button
                  variant="tertiary"
                  className="w-full !justify-end"
                  status="nobg-red"
                  onClick={() => clearBasketConfirmationDrawer(product)}
                >
                  <TrashIcon /> Remove
                </Button>
              </div>
            </CartProductCard> 
          ))}
        </div>
        <div className="mt-[150px]">
          <Button 
            variant="primary" 
            className="w-full mt-4"
            href={`/shop/checkout`}
          >
            Pay £{cart.CartTotalFormatted}
          </Button>
          <Button
            variant="quaternary"
            theme=""
            className="w-full mt-4"
            href="/shop/my-participants"
          >
            Add Another Band
          </Button>
        </div>
        </div>
      )}
    </div>
  )
}

// Cart.propTypes = {
  
// }